<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <list-page-header>
        <div slot="right">
          <el-button
            :disabled="isSaving"
            size="mini"
            type="primary"
            icon="el-icon-back"
            @click="handleCancel"
          >
            {{ $t('button.back') }}
          </el-button>
          
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </div>
      </list-page-header>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <el-form
        v-loading="isLoading"
        ref="form"
        :disabled="isSaving"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="top"
        label-width="200"
      >
        <h4 class="title">
          Order Detail
        </h4>

        <div class="form-group">
          <el-form-item
            label="Client"
            prop="private_client_id"
          >
            <el-select
              :placeholder="$t('placeholder.select')"
              :disabled="isLoading || id"
              v-model="form.private_client_id"
              filterable
              @change="didChangeClient"
              style="width:250px"
            >
              <el-option
                v-for="item in clientList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </div>

        <div 
          v-if="isEditMode"
          class="form-group"
        >
          <el-form-item
            label="Recipient Name"
            prop="recipient_name"
          >
            <el-input
              v-model="form.recipient_name"
            />
          </el-form-item>
        </div>

        <div 
          v-if="isEditMode"
          class="form-group"
        >
          <el-form-item
            label="Delivery Address"
            prop="delivery_address"
          >
            <el-input
              v-model="form.delivery_address"
            />
          </el-form-item>
        </div>
        
        <div 
          v-else
          class="form-group"
        >
          <el-form-item
            label="Delivery Address"
            prop="delivery_address_id"
          >
            <el-select
              :placeholder="$t('placeholder.select')"
              :disabled="isLoadingAddressList"
              v-model="form.delivery_address_id"
              style="width:500px"
              filterable
            >
              <el-option
                v-for="item in addressList"
                :key="item.id"
                :label="item.description"
                :value="item.id"
              >
                <span style="float:left">{{ item.description }}</span>
              </el-option>
            </el-select>
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Currency"
            prop="currency"
          >
            <el-select
              v-model="form.currency"
              :placeholder="$t('placeholder.select')"
              :disabled="isPaid"
            >
              <el-option
                v-for="currency in currencyList"
                :key="currency"
                :label="currency"
                :value="currency"
              />
            </el-select>
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Shipping Fee"
            prop="shipping_fee"
          >
            <el-input
              v-model="form.shipping_fee"
              :disabled="isPaid"
              style="width: 100px"
            />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Remarks"
            prop="remarks"
          >
            <el-input
              v-model="form.remarks"
              :disabled="isPaid"
              style="width: 300px"
            />
          </el-form-item>
        </div>

        <h4 
          v-if="isPaid"
          class="title"
        >
          Shipment To Private Client
        </h4>

        <div 
          v-if="isPaid"
          class="form-group"
        >
          <el-form-item
            label="Courier"
            prop="courier_slug"
          >
            <el-select
              :placeholder="$t('placeholder.select')"
              :disabled="isLoading"
              v-model="form.courier_slug"
              filterable
            >
              <el-option
                v-for="item in courierList"
                :key="item.id"
                :label="item.name"
                :value="item.slug"
              />
            </el-select>
          </el-form-item>
        </div>
        
        <div 
          v-if="isPaid"
          class="form-group"
        >
          <el-form-item
            label="Tracking Number"
            prop="tracking_number"
          >
            <el-input
              v-model="form.tracking_number"
              style="width: 200px"
            />
          </el-form-item>
        </div>

        <div 
          v-if="isPaid"
          class="form-group"
        >
          <el-button
            :disabled="isSaving"
            size="mini"
            type="info"
            icon="el-icon-plus"
            style="margin-top: 10px;"
            @click="handleAddDelivery"
          >
            Add Delivery Cost
          </el-button>
        </div>

        <el-table
          v-if="isPaid"
          :data="form.deliveries"
          style="margin-bottom: 20px;"
        >
          <el-table-column
            align="center"
            prop="type"
            label="Type"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-radio v-model="scope.row.type" label="deliver">Deliver</el-radio>
              <el-radio v-model="scope.row.type" label="return">Return</el-radio>
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="delivery_cost"
            label="Delivery Cost"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-input
                v-model.trim="scope.row.delivery_cost"
                clearable
              />
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="remarks"
            label="Remarks"
            min-width="200"
          >
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.remarks"
                clearable
              />
            </template>
          </el-table-column>
        </el-table>

        <div 
          v-if="!isEditMode && hasCreditBalance"
          class="form-group"
        >
          <el-form-item
            label="Pay by credit?"
            prop="is_use_credit"
          >
            <el-switch 
              v-model="form.is_use_credit"
              :disabled="form.currency != 'HKD'" 
            /> 
          </el-form-item>
        </div>

        <div 
          v-if="isEditMode"
          class="form-group"
        >
          <el-form-item
            label="Split Payment"
            prop="is_split_payment"
          >
            <el-switch 
              v-model="form.is_split_payment" 
              @change="handleSplitPaymentChange(form)"
              :disabled="isPaid || hasCreditPayment"
            /> 
          </el-form-item>
        </div>

        <template v-if="form.is_split_payment">
          <h4 class="title">Split Payment</h4>
          <div 
            v-if="!isPaid || isPartialPaid"
            class="form-group"
          >
            <el-button
              :disabled="isSaving"
              size="mini"
              type="info"
              icon="el-icon-plus"
              style="margin-top: 10px;"
              @click="handleAddPaymentDetail"
            >
              Add Payment
            </el-button>
          </div>

          <el-table
            :data="form.payment_details"
            style="margin-bottom: 20px;"
          >
            <el-table-column
              align="center"
              prop="currency"
              label="Currency"
              min-width="80"
            >
              <template slot-scope="scope">
                {{ scope.row.currency }}
              </template>
            </el-table-column>

            <el-table-column
              align="center"
              prop="amount"
              label="Amount"
              min-width="100"
            >
              <template slot-scope="scope">
                <el-input
                  v-model.trim="scope.row.amount"
                  clearable
                  :disabled="!!scope.row.paid || !!scope.row.paid_by_credit"
                />
              </template>
            </el-table-column>

            <el-table-column
              align="center"
              prop="remarks"
              label="Remarks"
              min-width="100"
            >
              <template slot-scope="scope">
                {{ scope.row.remarks }}
              </template>
            </el-table-column>

            <el-table-column
              label="Paid"
              align="center"
              width="60"
            >
              <template slot-scope="scope">
                <i 
                  v-if="!!scope.row.paid"
                  class="el-icon-check"
                />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('table.action')"
              align="center"
              width="60"
            >
              <template slot-scope="scope">
                <el-button
                  v-if="!scope.row.paid && !scope.row.paid_by_credit"
                  size="mini"
                  type="danger"
                  icon="el-icon-delete"
                  circle
                  @click="handleDeletePaymentDetail(scope.$index)"
                />
              </template>
            </el-table-column>
          </el-table>
        </template>

        <h4 class="title">
          Product List
        </h4>
        
        <div 
          v-if="!isPaid"
          class="form-group"
        >
          <el-button
            :disabled="isSaving"
            size="mini"
            type="info"
            icon="el-icon-plus"
            style="margin-top: 10px;"
            @click="handleAddItem"
          >
            Add Product
          </el-button>
        </div>

        <el-table
          :data="form.items"
          style="margin-bottom: 20px;"
        >
          <el-table-column
            align="center"
            prop="brand_name"
            label="Brand Name"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.brand_name"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>
          
          <el-table-column
            align="center"
            prop="product_name"
            label="Product Name"
            min-width="150"
          >
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.product_name"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="product_gender"
            label="Gender"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-select
                v-model="scope.row.product_gender"
                :disabled="isPaid"
                :placeholder="$t('placeholder.select')"
              >
              <el-option
                v-for="productGender in productGenderList"
                :key="productGender"
                :label="productGender"
                :value="productGender"
              />
            </el-select>
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="product_category"
            label="Category"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-select
                v-model="scope.row.product_category"
                :disabled="isPaid"
                :placeholder="$t('placeholder.select')"
              >
              <el-option
                v-for="productCategory in productCategoryList"
                :key="productCategory"
                :label="productCategory"
                :value="productCategory"
              />
            </el-select>
            </template>
          </el-table-column>
          
          <el-table-column
            align="center"
            prop="size"
            label="Size"
            min-width="80"
          >
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.size"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>
          
          <el-table-column
            align="center"
            prop="remarks"
            label="Remarks"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.remarks"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>
          
          <el-table-column
            align="center"
            prop="quantity"
            label="Quantity"
            min-width="80"
          >
            <template slot-scope="scope">
              <el-input
                v-model.trim="scope.row.quantity"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>
          
          <el-table-column
            align="center"
            prop="currency"
            label="Currency"
            min-width="80"
          />
          
          <el-table-column
            align="center"
            prop="unit_price"
            label="Unit Price"
            min-width="100"
          >
            <template slot-scope="scope">
              <el-input
                v-model.trim="scope.row.unit_price"
                clearable
                :disabled="isPaid"
              />
            </template>
          </el-table-column>

          <el-table-column
            v-if="!isPaid"
            :label="$t('table.action')"
            align="center"
            width="60"
          >
            <template #default="{ $index }">
              <el-button
                size="mini"
                type="danger"
                icon="el-icon-delete"
                circle
                @click="handleDeleteItem($index)"
              />
            </template>
          </el-table-column>
        </el-table>

        <div v-if="isPaid">
          <h4 class="title">
            External Order List
          </h4>
          
          <div class="form-group">
            <el-button
              :disabled="isSaving"
              size="mini"
              type="info"
              icon="el-icon-plus"
              style="margin-top: 10px;"
              @click="handleGeneratePaylink"
            >
              Generate Paylink (Create Merchant Order)
            </el-button>
          </div>

          <div v-for="external_order in form.external_orders">
            <div class="form-group">
              <el-form-item
                label="Merchant"
                prop="store_id"
              >
                <el-select
                  :placeholder="$t('placeholder.select')"
                  :disabled="isLoading || external_order.order_confirmation"
                  v-model="external_order.store_id"
                  filterable
                  @change="didChangeExternalOrderStore(external_order)"
                >
                  <el-option
                    v-for="item in storeList"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  >
                    <span style="float: left">{{ item.name }}</span>
                    <span style="float:right;font-size:13px;">{{ item.commission_range }}</span>
                  </el-option>
                </el-select>
              </el-form-item>
            </div>

            <div 
              class="form-group"
              v-if="external_order.paylink"
              style="margin-left: 10px;"
            >
              <a :href="external_order.paylink" target="_blank">
                <div style="text-decoration: underline; color: blue;">Open Paylink</div>
              </a>
            </div>

            <div class="form-group">
              <el-form-item
                label="Currency"
                prop="currency"
              >
                <el-select
                  v-model="external_order.currency"
                  :placeholder="$t('placeholder.select')"
                  :disabled="external_order.order_confirmation"
                  @change="didChangeExternalOrderCurrency(external_order)"
                >
                  <el-option
                    v-for="currency in currencyList"
                    :key="currency"
                    :label="currency"
                    :value="currency"
                    :disabled="true"
                  />
                </el-select>
              </el-form-item>
            </div>

            <div class="form-group">
              <el-form-item
                label="Merchant Order Number"
                prop="order_number"
              >
                <el-input
                  v-model="external_order.order_number"
                  :disabled="external_order.order_confirmation"
                  style="width: 200px"
                />
              </el-form-item>
            </div>

            <div class="form-group">
              <el-form-item
                label="Merchant Tracking Number"
                prop="tracking_number"
              >
                <el-input
                  v-model="external_order.tracking_number"
                  style="width: 200px"
                />
              </el-form-item>
            </div>

            <div 
              v-if="!external_order.order_confirmation"
              class="form-group"
            >
              <el-button
                :disabled="isSaving"
                size="mini"
                type="info"
                icon="el-icon-plus"
                style="margin-top: 10px;"
                @click="handleAddExternalOrderProduct(external_order)"
              >
                Add Product
              </el-button>
            </div>

            <el-table
              :data="external_order.items"
              style="margin-bottom: 20px;"
            >
              <el-table-column
                align="center"
                label="Product"
                min-width="200"
              >
                <template slot-scope="scope">
                  <el-select
                    :placeholder="$t('placeholder.select')"
                    :disabled="isLoading || external_order.order_confirmation"
                    v-model="scope.row.order_item_id"
                    filterable
                    style="min-width:200px;"
                  >
                    <el-option
                      v-for="item in form.items"
                      :key="item.id"
                      :label="item.description"
                      :value="item.id"
                    />
                  </el-select>
                </template>
              </el-table-column>

              <el-table-column
                align="center"
                prop="remarks"
                label="Remarks"
                min-width="200"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.remarks"
                    :disabled="external_order.order_confirmation"
                    clearable
                  />
                </template>
              </el-table-column>
              
              <el-table-column
                align="center"
                prop="quantity"
                label="Quantity"
                min-width="80"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model.trim="scope.row.quantity"
                    :disabled="external_order.order_confirmation"
                    clearable
                  />
                </template>
              </el-table-column>
              
              <el-table-column
                align="center"
                prop="currency"
                label="Currency"
                min-width="80"
              >
                {{ external_order.currency }}
              </el-table-column>
              
              <el-table-column
                align="center"
                prop="unit_price"
                label="Unit Price"
                min-width="100"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model.trim="scope.row.unit_price"
                    :disabled="external_order.order_confirmation"
                    clearable
                  />
                </template>
              </el-table-column>
              
              <el-table-column
                v-if="!external_order.order_confirmation"
                :label="$t('table.action')"
                align="center"
                width="60"
              >
                <template #default="{ $index }">
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-delete"
                    circle
                    @click="handleDeleteExternalOrderProduct(external_order, $index)"
                  />
                </template>
              </el-table-column>
            </el-table>

            <div class="form-group">
              <el-button
                :disabled="isSaving"
                size="mini"
                type="info"
                icon="el-icon-plus"
                style="margin-top: 10px;"
                @click="handleAddExternalOrderShipment(external_order)"
              >
                Add Shipment
              </el-button>
            </div>

            <el-table
              :data="external_order.shipments"
              style="margin-bottom: 20px;"
            >
              <el-table-column
                align="center"
                prop="shipping_fee"
                label="Shipping Fee"
                min-width="100"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model.trim="scope.row.shipping_fee"
                    clearable
                  />
                </template>
              </el-table-column>

              <el-table-column
                align="center"
                prop="remarks"
                label="Remarks"
                min-width="200"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.remarks"
                    clearable
                  />
                </template>
              </el-table-column>
            </el-table>

            <div
              v-if="external_order.items.length > 0"
              class="form-group"
            >
              <el-form-item
                label="Order Confirmation"
                prop="order_confirmation"
              >
                <el-upload
                  v-loading="isUploading"
                  v-model="external_order.order_confirmation"
                  :file-list="external_order.files"
                  :limit="1"
                  :disabled="isUploading"
                  :http-request="handleUpload"
                  :before-upload="handleBeforeUpload"
                  :on-exceed="handleUploaderLimitExceed"
                  :on-error="uploadFailed"
                  :on-success="uploadSuccessful"
                  list-type="picture"
                  drag
                  accept=".png, .jpg, .pdf"
                  action="noneed"
                  :data="{external_order: external_order}"
                >
                  <i class="el-icon-upload" />
                  <div class="el-upload__text">Drop File here or <em>click to upload</em></div>
                </el-upload>
              </el-form-item>
            </div>

            <hr />
          </div>
        </div>
      </el-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import { createComponent, computed, reactive, ref, watch, onBeforeMount } from '@vue/composition-api'
import ListPageHeader from '@/views/layout/components/ListPageHeader.vue'
import clipboard from '@/directive/clipboard'

import PrivateClientsService from '../../api/private-clients'
import { i18n } from '@/lang'
import {validateIsNonNegativeNumber} from '../../utils/element-ui-form-validators'

const PrivateClientOrderDetail = createComponent({
  name: 'PrivateClientOrderDetail',
  components: {
    ListPageHeader,
    clipboard,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  setup({ id }, { root, refs }) {
    const { $router } = root

    const isEditMode = ref(!!id)
    const isLoading = ref(false)
    const isLoadingAddressList = ref(false)
    const isSaving = ref(false)
    const isPaid = ref(false)
    const isPartialPaid = ref(false)
    const isUploading = ref(false)
    const hasCreditBalance = ref(false)
    const hasCreditPayment = ref(false)

    const currencyList = ['HKD','GBP']
    const productGenderList = ['Men', 'Women', 'Kids']
    const productCategoryList = ['Top', 'Bottom', 'Shoes', 'Bag', 'Watch', 'Jewellery', 'Accessories', 'Beauty', 'Lifestyle']

    const form = reactive({
      private_client_id: null,
      recipient_name: null,
      delivery_address: null,
      delivery_address_id: null,
      currency: null,
      shipping_fee: 0,
      remarks: null,
      courier_slug: null,
      tracking_number: null,
      items: [],
      is_split_payment: false,
      is_use_credit: false,
      payment_details: [],
      external_orders: [],
      deliveries: [],
    })

    watch(() => form.currency, (currName, prevName) => {
      for (let i = 0; i < form.items.length; i++) {
        form.items[i]['currency'] = currName
      }

      for (let i = 0; i < form.payment_details.length; i++) {
        form.payment_details[i]['currency'] = currName
      }

      if (currName !== 'HKD') {
        form.is_use_credit = false
      }
    })

    const formRules = reactive({
      private_client_id: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      delivery_address_id: [{ required: (!id), message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      recipient_name: [{ required: (id), message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      delivery_address: [{ required: (id), message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      currency: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      shipping_fee: [
        { required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' },
        { required: true, validator: validateIsNonNegativeNumber, trigger: 'blur' }
      ],
    })

    const getDefaultItem = () => ({
      brand_name: null,
      product_name: null,
      quantity: 1,
      size: null,
      remarks: null,
      currency: form.currency,
      unit_price: 0,
    })

    const getDefaultDeliveryDetail = () => ({
      id: 0,
      type: 'deliver',
      delivery_cost: 0,
      remarks: null,
    })

    const getDefaultPaymentDetail = () => ({
      currency: form.currency,
      amount: 0,
    })

    const getDefaultExternalOrder = () => ({
      id: 0,
      store_id: null,
      paylink: null,
      currency: form.currency,
      order_number: null,
      tracking_number: null,
      order_confirmation: null,
      files: [],
      items: [],
      shipments: [],
    })

    const getDefaultExternalOrderItem = () => ({
      id: 0,
      order_item_id: null,
      remarks: null,
      quantity: 1,
      unit_price: 0,
    })

    const getDefaultExternalOrderShipment = () => ({
      id: 0,
      shipping_fee: 0,
      remarks: null,
    })

    const goToListPage = () => {
      $router.push({ path: '/private-client/order/list' })
    }

    const handleSaveSuccess = () => {
      isSaving.value = false
      
      goToListPage()
    }
    
    const handleSaveFail = (error) => {
      isSaving.value = false

      root.$alert(`Error: ${error}`, root.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: root.$t('button.ok'),
      })
    }

    const clientList = ref([])

    const getClientList = async () => {
      isLoading.value = true
      
      const { data } = await PrivateClientsService.getClientList()

      clientList.value = data
      
      isLoading.value = false
    }

    const courierList = ref([])

    const getCourierList = async () => {
      const { data } = await PrivateClientsService.getCourierList()

      courierList.value = data
    }

    const addressList = ref([])

    const getAddressList = async () => {
      isLoadingAddressList.value = true

      const { data } = await PrivateClientsService.getAddressList(form.private_client_id)

      isLoadingAddressList.value = false

      data.forEach(address => {
        address.description = `${address.nickname} | ${address.name} | ${address.address}`

        if (!id && address.is_default === 1) {
          form.delivery_address_id = address.id
        }
      })

      addressList.value = data
    }

    const storeList = ref([])

    const getOrderStoreList = async (orderId) => {
      isLoading.value = true

      const { data } = await PrivateClientsService.getOrderStoreList(orderId)
      storeList.value = data

      isLoading.value = false
    }

    onBeforeMount(async () => {
      getClientList()
      getCourierList()

      if (id) {
        getOrderStoreList(id)
        getDetail(id)
      }
    })

    const getDetail = async (orderId) => {
      isLoading.value = true

      try {
        const { data } = await PrivateClientsService.getOrderDetail(orderId)

        isPaid.value = (![1].includes(data.payment_status))
        isPartialPaid.value = ([5].includes(data.payment_status))
        form.is_split_payment = (data.payment_details.length > 1)
        hasCreditPayment.value = data.payment_details.some((payment_detail) => payment_detail.paid_by_credit == 1)

        Object.keys(data).forEach((key) => {
          // if (key === 'type' && !data[key]) return
          form[key] = data[key]
          const { client } = data

          if ('id' in client) {
            form['private_client_id'] = client.id
          }
        })

        if (!form.is_split_payment) {
          form.payment_details = []
        }

        for (let i = 0; i < form.items.length; i++) {
          form.items[i].description = `${form.items[i].brand_name} - ${form.items[i].product_name}`

          if (form.items[i].size !== null && form.items[i].size !== '') {
            form.items[i].description = `${form.items[i].description} (${form.items[i].size})`
          }
        }

        for (let i = 0; i < form.external_orders.length; i++) {
          if (form.external_orders[i].store_id > 0) {
            didChangeExternalOrderStore(form.external_orders[i])
          }

          let orderConfirmation = form.external_orders[i].order_confirmation

          if (orderConfirmation !== null && orderConfirmation !== '') {
            form.external_orders[i].files = [{
              name: '',
              url: orderConfirmation
            }]
          }
        }
      } catch (error) {
        console.log(error)

        root.$alert(root.$t('error.somethingWentWrong'), root.$t('button.error'), {
          confirmButtonText: root.$t('button.ok'),
          type: 'error',
        }).then(() => {
          goToListPage()
        })
      } finally {
        isLoading.value = false
      }
    }

    const saveOrder = async (orderForm) => {
      isSaving.value = true

      // Validate external order items
      let errorMessage = null

      form.items.forEach(item => {
        let title = `${item.brand_name} - ${item.product_name}`
        let availableQuantity = item.quantity
        let greatestUnitPrice = 0

        form.external_orders.forEach(externalOrder => {
          externalOrder.items.forEach(externalOrderItem => {
            if (externalOrderItem.order_item_id === item.id) {
              availableQuantity -= externalOrderItem.quantity
              greatestUnitPrice = Math.max(greatestUnitPrice, externalOrderItem.unit_price)
            }
          })
        })

        if (availableQuantity < 0) {
          errorMessage = `${title}: Exceed original order quantity.`
        }

        if (greatestUnitPrice > item.unit_price) {
          errorMessage = `${title}: Exceed original order unit price.`
        }
      })

      // Validate overall order earning
      let totalIncome = parseFloat(form.shipping_fee)
      let totalExpense = parseFloat(form.delivery_cost)

      form.items.forEach(item => {
        totalIncome += parseFloat(item.unit_price * item.quantity)
      })

      form.external_orders.forEach(externalOrder => {
        externalOrder.shipments.forEach(externalOrderShipment => {
          totalExpense += parseFloat(externalOrderShipment.shipping_fee)
        })

        externalOrder.items.forEach(externalOrderItem => {
          totalExpense += parseFloat(externalOrderItem.unit_price * externalOrderItem.quantity)
        })
      })

      if (totalExpense > totalIncome * 0.95) {
        errorMessage = `This order is losing money.`
      }

      if (errorMessage === null) {
        try {
          if (id) {
            await PrivateClientsService.updateOrder(orderForm, id)
          } else {
            await PrivateClientsService.createOrder(orderForm)
          }

          handleSaveSuccess()
        } catch (error) {
          handleSaveFail(error)
        }
      } else {
        handleSaveFail(errorMessage)
      }
    }

    const handleCancel = () => goToListPage()

    const handleSave = () => {
      refs.form.validate().then(() => {
        if (!validateItems()) {
          root.$message({
            message: 'Please fill in all Product Name / Quantity / Unit Price',
            type: 'error',
          })
          return
        }
        if (!validatePaymentDetails()){
          root.$message({
            message: 'Sum of Split Payments need to equal to order total',
            type: 'error',
          })
          return
        }
        saveOrder(form)
      }).catch((error) => {
        console.log(error)

        root.$alert(root.$t('error.pleaseFillInAndCheckErrors'), root.$t('common.oops'), {
          confirmButtonText: root.$t('button.ok'),
        })
      })
    }

    const validateItems = () => {
      for (let i = 0; i < form.items.length; i++) {
        let item = form.items[i]
        const hasValidInput = !!item.product_name || !!item.quantity || !!item.unit_price
        
        if (!hasValidInput) {
          return false
        }
      }

      return true
    }

    const validatePaymentDetails = () => {
      if (!form.is_split_payment) {
        return true
      }

      let orderTotal = parseFloat(form.shipping_fee)
      for (let i = 0; i < form.items.length; i++) {
        let item = form.items[i]
        orderTotal += (parseFloat(item.unit_price) * parseInt(item.quantity))
      }

      let splitPaymentTotal = 0
      for (let i = 0; i < form.payment_details.length; i++) {
        let paymentDetail = form.payment_details[i]
        if (!paymentDetail.amount) {
          return false
        }
        splitPaymentTotal += parseFloat(paymentDetail.amount)
      }

      return (orderTotal.toFixed(2) === splitPaymentTotal.toFixed(2))
    }

    const handleAddItem = () => {
      form.items.push(getDefaultItem())
    }

    const handleDeleteItem = (index) => {
      form.items.splice(index, 1)
    }

    const handleSplitPaymentChange = (form) => {
      if (!form.is_split_payment) {
        form.payment_details = []
      }
    }

    const handleAddDelivery = () => {
      console.log('handleAddDelivery')
      form.deliveries.push(getDefaultDeliveryDetail())
    }

    const handleAddPaymentDetail = () => {
      form.payment_details.push(getDefaultPaymentDetail())
    }

    const handleDeletePaymentDetail = (index) => {
      form.payment_details.splice(index, 1)
    }

    const handleGeneratePaylink = () => {
      form.external_orders.push(getDefaultExternalOrder())
    }

    const handleAddExternalOrderProduct = (externalOrder) => {
      externalOrder.items.push(getDefaultExternalOrderItem())
    }

    const handleAddExternalOrderShipment = (externalOrder) => {
      externalOrder.shipments.push(getDefaultExternalOrderShipment())
    }

    const handleDeleteExternalOrderProduct = (externalOrder, index) => {
      externalOrder.items.splice(index, 1)
    }

    const handleCopySuccess = () => {
      root.$message('Copied Link Successfully')
    }

    const didChangeClient = () => {
      form.recipient_name = null
      form.delivery_address = null
      form.delivery_address_id = null

      hasCreditBalance.value = false

      clientList.value.forEach(client => {
        if (form.private_client_id === client.id && client.credit_balance > 0) {
          hasCreditBalance.value = true
        }
      })

      getAddressList()
    }

    const didChangeExternalOrderStore = (externalOrder) => {
      storeList.value.forEach(store => {
        if (store.id === externalOrder.store_id) {
          externalOrder.paylink = store.paylink
        }
      })
    }

    const didChangeExternalOrderCurrency = (externalOrder) => {
      for (let i = 0; i < externalOrder.items.length; i++) {
        externalOrder.items[i].currency = externalOrder.currency
      }
    }

    //

    const handleBeforeUpload = (file) => {
      if (!file.name.includes('.png') && !file.name.includes('.jpg') && !file.name.includes('.jpeg') && !file.name.includes('.pdf')) {
        root.$alert(
          'Please double check you are uploading a .png, .jpg, .jpeg or .pdf file',
          'Incorrect File Type', {
            type: 'error',
            confirmButtonText: 'OK',
          },
        )
        return false
      }

      return true
    }

    const handleUpload = async (uploadAction) => {
      isUploading.value = true

      try {
        uploadAction.data.external_order.order_confirmation = uploadAction.file
        uploadAction.onSuccess()
      } catch (error) {
        uploadAction.onError(error)
      }
    }

    const handleUploaderLimitExceed = () => {
      root.$alert(
        'Sorry, please upload one file at a time!',
        'Too Many Files', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    }

    const uploadSuccessful = () => {
      isUploading.value = false
    }

    const uploadFailed = (error) => {
      console.log(error)

      isUploading.value = false
      
      root.$alert(
        'Please try again later.',
        'Upload Failed', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    }

    return {
      form,
      currencyList,
      productGenderList,
      productCategoryList,
      clientList,
      courierList,
      addressList,
      storeList,
      formRules,

      isEditMode,
      isLoading,
      isLoadingAddressList,
      isSaving,
      isPaid,
      isPartialPaid,
      isUploading,
      hasCreditBalance,
      hasCreditPayment,

      handleCancel,
      handleSave,
      handleAddItem,
      handleDeleteItem,
      handleSplitPaymentChange,
      handleAddDelivery,
      handleAddPaymentDetail,
      handleDeletePaymentDetail,
      handleGeneratePaylink,
      handleAddExternalOrderProduct,
      handleAddExternalOrderShipment,
      handleDeleteExternalOrderProduct,
      handleCopySuccess,
      didChangeClient,
      didChangeExternalOrderStore,
      didChangeExternalOrderCurrency,

      handleBeforeUpload,
      handleUpload,
      handleUploaderLimitExceed,
      uploadSuccessful,
      uploadFailed,
    }
  },
})

export default PrivateClientOrderDetail
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";
</style>
